import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Every list keeps its filters in the URL (doc 08 shared behaviours), so a
 * filtered dashboard can be bookmarked and the back button restores it.
 */
export function useUrlState<T extends Record<string, string>>(defaults: T) {
  const [params, setParams] = useSearchParams();

  const state = useMemo(() => {
    const out = { ...defaults } as Record<string, string>;
    for (const key of Object.keys(defaults)) {
      const v = params.get(key);
      if (v !== null) out[key] = v;
    }
    return out as T;
  }, [params, defaults]);

  const set = useCallback(
    (patch: Partial<T>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v === undefined || v === '' || v === defaults[k]) next.delete(k);
            else next.set(k, String(v));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams, defaults],
  );

  return [state, set] as const;
}

/** Sort state stored as "column:asc" in ?sort=. */
export function parseSort(value: string): { column: string; dir: 'asc' | 'desc' } | null {
  if (!value) return null;
  const [column = '', dir = 'asc'] = value.split(':');
  if (!column) return null;
  return { column, dir: dir === 'desc' ? 'desc' : 'asc' };
}
